/**
 * Router minimale basato su `location.hash`.
 *
 * Supporta route con un segmento: `#/calculator`, `#/legend`, `#/settings`.
 * Emette un evento custom `route-change` sul `window` quando cambia la rotta.
 */
export type RouteListener = (route: Routes) => void;

export enum Routes {
  EMPTY = '',
  CALCULATOR = 'calculator',
  LEGEND = 'legend',
  SETTINGS = 'settings'
}


export class HashRouter {
  static readonly ROUTES = [Routes.CALCULATOR, Routes.LEGEND,/* Routes.SETTINGS*/] as const;
  static readonly DEFAULT_ROUTE = Routes.CALCULATOR;

  private listeners = new Set<RouteListener>();

  constructor() {
    window.addEventListener('hashchange', this.handleHashChange);
    // Il primo trigger manuale serve quando si carica la pagina senza hash.
    window.addEventListener('DOMContentLoaded', this.handleHashChange);
  }

  /** Restituisce la rotta attuale normalizzata (senza `#/`). */
  current(): Routes {
    const raw = window.location.hash.replace(/^#\/?/, '').split('?')[0] as Routes;
    return (HashRouter.ROUTES as readonly Routes[]).includes(raw) ? raw : HashRouter.DEFAULT_ROUTE;
  }

  /** Naviga verso una rotta. */
  navigate(route: Routes): void {
    if (!(HashRouter.ROUTES as readonly Routes[]).includes(route)) {
      route = HashRouter.DEFAULT_ROUTE;
    }
    const target = `#/${route}`;
    if (window.location.hash === target) {
      // Forza comunque un emit (es. click sul link attivo)
      this.handleHashChange();
    } else {
      window.location.hash = target;
    }
  }

  subscribe(fn: RouteListener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  destroy(): void {
    window.removeEventListener('hashchange', this.handleHashChange);
    window.removeEventListener('DOMContentLoaded', this.handleHashChange);
  }

  private handleHashChange = (): void => {
    const route = this.current();
    this.listeners.forEach((l) => l(route));
  };
}
